import type { GraphNode } from '@/lib/apiTypes'

/**
 * Where a graph node lives in the portal.
 *
 * Extracted from ModuleDetailPanel, which held it as an inline switch, because the edge detail
 * modal needs the same answer for both of an edge's endpoints. Keeping one copy is not tidiness:
 * the comment on that component records that one of these three branches used to point at
 * `/modules/{ns}/modules/...`, a route nothing matched, so it fell through to the catch-all and
 * bounced to the dashboard. A route is a thing worth being able to unit-test.
 *
 * Returns null where there is nowhere honest to go — an external module we hold no page for, or
 * a kind whose page needs an org slug we were not given.
 */
export function graphNodeHref(node: GraphNode, currentOrg?: string): string | null {
  if (node.external) return null

  if (node.kind === 'module') {
    if (!node.module_name || !node.module_provider) return null
    // The version segment is optional: the module page resolves its own latest without it.
    const version = node.latest_version ? `/${node.latest_version}` : ''
    return `/org/${node.namespace}/modules/${node.module_name}/${node.module_provider}${version}`
  }

  if (node.kind === 'state') {
    if (!node.workspace_name || !currentOrg) return null
    return `/org/${currentOrg}/state/${encodeURIComponent(node.workspace_name)}`
  }

  // Providers have no page of their own; the Providers list takes a search term instead.
  if (!currentOrg || !node.provider_type) return null
  const q = encodeURIComponent(`${node.namespace}/${node.provider_type}`)
  return `/org/${currentOrg}/providers?q=${q}`
}
